import CheckIcon from "@mui/icons-material/Check";
import { Stack, type Theme, Typography } from "@mui/material";
import { GoalIcon } from "Components/Analyses/_Shared/Goals/GoalIcon";
import { Flex } from "Components/_Shared/Core/Flex";
import { HStack } from "Components/_Shared/Core/Stack";
import { type GoalOverview, GoalOverviewPriorityLevel, GoalOverviewProgressStatus } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { GoalCardActions } from "./GoalCardActions";
import { TimelinePill } from "./TimelinePill";
import { formatRelativeTime } from "./utils/dates";
import { type GoalTone, getGoalTone } from "./utils/goalTone";
import { getTimelineGoalTypeLabel } from "./utils/goalTypeTranslation";

export const ICON_TILE_SIZE = 40;

const ICON_TILE_COLORS: Record<GoalTone, { bgcolor: string; color: string }> = {
  active: { bgcolor: "primary.light", color: "primary.main" },
  completed: { bgcolor: "success.light", color: "success.dark" },
  archived: { bgcolor: "grey.100", color: "text.secondary" },
};
const HEADER_GAP = Spacing.L;

/** Left offset of the card content below the header, so it lines up with the goal name. */
export const alignWithGoalName = (theme: Theme) => `calc(${ICON_TILE_SIZE}px + ${theme.spacing(HEADER_GAP)})`;

const Timeline = Resources.AnalysisDetail.Goals.Timeline;

type Props = {
  goal: GoalOverview;
  onEdit?: () => void;
  isTablet: boolean;
};

export const GoalCardHeader = ({ goal, onEdit, isTablet }: Props) => {
  const { name, type, priorityLevel, progressStatus, targetDate } = goal;
  const isCompleted = progressStatus === GoalOverviewProgressStatus.completed;
  const { t } = useResource();

  const typeLabel = t(getTimelineGoalTypeLabel(goal));
  const subtitle = targetDate
    ? t(Timeline.TypeWithTime, { type: typeLabel, time: formatRelativeTime(targetDate, new Date(), t) })
    : typeLabel;

  return (
    <HStack alignItems="center" gap={HEADER_GAP}>
      <Flex
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        width={ICON_TILE_SIZE}
        height={ICON_TILE_SIZE}
        borderRadius={Radius.L}
        {...ICON_TILE_COLORS[getGoalTone(goal)]}
        sx={{ "& svg": { width: 22, height: 22 } }}
      >
        <GoalIcon type={type} />
      </Flex>

      <Stack flex="0 1 auto" minWidth={0} gap={Spacing.XS}>
        <HStack alignItems="center" gap={Spacing.M}>
          <Typography fontSize={17} fontWeight={700} color="text.primary">
            {name}
          </Typography>
          {priorityLevel === GoalOverviewPriorityLevel.high && (
            <TimelinePill bgcolor="primary.light" color="primary.main">
              {t(Timeline.Priority)}
            </TimelinePill>
          )}
        </HStack>
        <Typography fontSize={13} color="text.secondary">
          {subtitle}
        </Typography>
      </Stack>

      {isCompleted && (
        <TimelinePill bgcolor="success.light" color="success.dark">
          <CheckIcon sx={{ fontSize: 13 }} />
          {t(Timeline.Completed)}
        </TimelinePill>
      )}

      <GoalCardActions goal={goal} onEdit={onEdit} isTablet={isTablet} />
    </HStack>
  );
};
